import { call, delay, fork, put, select, take } from 'redux-saga/effects';
import { selectIsSystemSession } from 'features/application/selectors';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import { MediaDevicesPermissions } from 'features/user-media/types';
import { selectAllMediaDevicePermissions } from 'features/user-media/userMediaSlice';
import { openModal } from 'features/modal/modalSlice';
import { roomRendered } from 'features/room/actions';
import { inviteParticipantSuggestionSaga } from 'features/room/sagas/inviteParticipantSuggestionSaga';
import { waitingEntryNotificationUpdateRequested } from 'features/notifications/actions';
import { User } from 'features/users/types';
import { selectAwaitingEntryUsers } from 'features/users/selectors/selectAwaitingEntryUsers';
import { board } from 'utils/whiteboard/BoardStateManager';
import { selectIsBreakoutRoom } from 'features/breakout-rooms/breakoutRoomsSlice';
import { handleSystemSessionSaga } from 'features/room/sagas/handleSystemSessionSaga';
import { selectTranscriptionStarted } from 'features/transcripts/slices/transcription/transcriptionSlice';
import { onTranscriptionStartedSaga } from 'features/transcripts/sagas/onTranscriptionStartedSaga';

export function* onRoomJoinedSaga() {
  board.shouldInitStore = true;

  const transcriptionStarted: boolean = yield select(selectTranscriptionStarted);

  const isSystemSession: boolean = yield select(selectIsSystemSession);
  if (isSystemSession) {
    yield call(handleSystemSessionSaga);

    if (transcriptionStarted) {
      yield call(onTranscriptionStartedSaga);
    }
    return;
  }

  yield take(roomRendered);

  const isBreakoutRoom: boolean = yield select(selectIsBreakoutRoom);
  if (!isBreakoutRoom) {
    const canBroadcastVideo: boolean = yield call(
      hasPermissionsSaga,
      PermissionTypes.broadcastVideo
    );
    const canBroadcastAudio: boolean = yield call(
      hasPermissionsSaga,
      PermissionTypes.broadcastAudio
    );

    if (canBroadcastVideo || canBroadcastAudio) {
      const permissions: MediaDevicesPermissions = yield select(selectAllMediaDevicePermissions);

      const hasVideoIssue = canBroadcastVideo && permissions.videoinput === 'denied';
      const hasAudioIssue = canBroadcastAudio && permissions.audioinput === 'denied';

      if (hasVideoIssue || hasAudioIssue) {
        yield put(
          openModal('mediaDevicesBlocked', {
            video: hasVideoIssue,
            audio: hasAudioIssue,
          })
        );
      }
    }
  }

  // toast container might not be initialized yet
  // @TODO refactor to wait for a layout render if it becomes an issue
  yield delay(250);

  yield fork(inviteParticipantSuggestionSaga);

  const awaitingUsers: User[] = yield select(selectAwaitingEntryUsers);
  if (awaitingUsers.length) {
    yield put(waitingEntryNotificationUpdateRequested());
  }

  if (transcriptionStarted) {
    yield fork(onTranscriptionStartedSaga);
  }
}
